import { useEffect, useState } from 'react'

import dayjs from 'dayjs'

import { useAppContext } from '../context/AppContext'
import { debugError, debugLog } from '../utils/debug'
import { getStringToBase32Code } from '../utils/getStringToBase32Code'

import { useEsConfig } from './useEsConfig'

/**
 * Hook to check if the event is locked based on unlock codes
 * Returns true if locked, false if unlocked
 */
export function useEventLock(): boolean {
  const { contentBundle } = useAppContext()
  const esConfig = useEsConfig()

  // Get code from query parameters
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')

  const [locked, setLocked] = useState<boolean>(true)

  useEffect(() => {
    async function checkEventLock(): Promise<void> {
      if (!contentBundle || !esConfig) {
        return
      }

      // If no event info, remain locked
      const eventInfo = contentBundle.get(esConfig.eventInfo)
      if (!eventInfo) {
        return
      }

      // Get interactive ID and unlocked status
      const interactiveId = eventInfo.interactiveId
      const eventUnlocked = eventInfo.unlocked

      // If event is unlocked, allow access
      if (eventUnlocked) {
        setLocked(false)
        return
      }

      // If no code provided, and event is set to locked, deny access
      if (!code && !eventUnlocked) {
        setLocked(true)
        return
      }

      try {
        // Calculate two codes, a default code and a user-day code
        const code1 = await getStringToBase32Code(interactiveId)
        debugLog('Unlocking available with default code:', code1)

        // day.js uses local time by default, so yyyy-mm-dd format in local time
        const today = dayjs().format('YYYY-MM-DD')
        debugLog('Unlock Date in local time:', today)

        const code2 = await getStringToBase32Code(`${interactiveId}${today}`)
        debugLog('Unlocking available with user-day code:', code2)

        // Check if provided code matches either calculated code
        const isValidCode = code === code1 || code === code2

        setLocked(!isValidCode)
        debugLog('Event is unlocked:', isValidCode)
      } catch (error) {
        debugError('Error unlocking event:', error)
        setLocked(true)
      }
    }

    checkEventLock()
  }, [contentBundle, esConfig, code])

  return locked
}
